import { Link } from "react-router-dom";
import { imageUrl, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

function lowestPrice(p) {
    if (p.product_type === "variant" && p.variants?.length) {
        const prices = p.variants
            .filter((v) => v.active)
            .map((v) => Number(v.sale_price || v.price));
        if (prices.length) return Math.min(...prices);
    }
    return Number(p.sale_price || p.price);
}

function listPrice(p) {
    if (p.product_type === "variant" && p.variants?.length) {
        const prices = p.variants.filter((v) => v.active).map((v) => Number(v.price));
        if (prices.length) return Math.min(...prices);
    }
    return Number(p.price);
}

function totalStock(p) {
    if (p.product_type === "variant" && p.variants?.length) {
        return p.variants.reduce((s, v) => s + (v.active ? Number(v.stock || 0) : 0), 0);
    }
    return Number(p.stock || 0);
}

export default function ProductCard({ product, testIdPrefix = "product-card" }) {
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const minPrice = lowestPrice(product);
    const lp = listPrice(product);
    const onSale = lp > minPrice;
    const stock = totalStock(product);
    const out = stock <= 0;
    const rawImg = product.primary_image || product.images?.[0] || "";
    const image = rawImg
        ? imageUrl(rawImg, "medium")
        : settings?.product_default_url
          ? imageUrl(settings.product_default_url, "medium")
          : "";

    return (
        <Link
            to={`/product/${product.slug}`}
            className="group block bg-[#121214] border border-[#27272A] rounded-sm overflow-hidden card-hover"
            data-testid={`${testIdPrefix}-${product.slug}`}
        >
            <div className="relative aspect-square bg-[#0A0A0B] overflow-hidden">
                {image ? (
                    <img
                        src={image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30 font-display uppercase tracking-widest text-xs">
                        No image
                    </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5">
                    {onSale && (
                        <span className="bg-[#FF3B30] text-white text-[10px] font-black uppercase px-2 py-1 tracking-widest">
                            Sale
                        </span>
                    )}
                    {product.new_arrival && !onSale && (
                        <span className="bg-[#34C759] text-black text-[10px] font-black uppercase px-2 py-1 tracking-widest">
                            New
                        </span>
                    )}
                </div>
                {out && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                        <span className="font-display uppercase tracking-widest text-white text-sm font-bold">
                            Out of stock
                        </span>
                    </div>
                )}
            </div>
            <div className="p-3 md:p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1 font-display">
                    {product.brand?.name || product.brand_name || ""}
                </div>
                <h3 className="text-white text-sm md:text-base font-medium leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-[#00BCE5] transition-colors">
                    {product.name}
                </h3>
                <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display font-bold text-white text-lg">
                        {formatPrice(minPrice, cur)}
                    </span>
                    {onSale && (
                        <span className="text-xs price-strike">{formatPrice(lp, cur)}</span>
                    )}
                </div>
            </div>
        </Link>
    );
}
